<template>
  <div class="reporting-filter">
    <div class="filter-section" v-if="!hideTypes">
      <div class="container-title">
        <p class="title">{{ $t('dash.reporting.filter.type') }} <span>({{ countTypesOn }})</span></p>
        <p class="reset" v-if="countTypesOn < typeList.length" @click="addAllTypes">{{ $t('dash.posts.filter.show-all') }}</p>
        <p class="reset" v-else @click="removeAllTypes">{{ $t('dash.posts.filter.hide-all') }}</p>
      </div>
      <div class="list">
        <div class="type" v-for="type in typeList" :key="type">
          <Checkbox :id="type" prefixId="rf" :isChecked="filter.types[type] !== false" :remove="toggleType" :add="toggleType" />
          <label :for="'rf' + type">{{ $t('dash.reporting.filter.type.' + type) }}</label>
        </div>
      </div>
    </div>
    <div class="filter-section">
      <div class="container-title">
        <p class="title">{{ $t('dash.reporting.filter.source') }}</p>
      </div>
      <div class="list source-list">
        <div class="type">
          <Checkbox id="solutionOnly" prefixId="rf" :isChecked="filter.solutionOnly" :remove="toggleSolution" :add="toggleSolution" />
          <label for="rfsolutionOnly">{{ $t('dash.reporting.filter.solution_only') }}</label>
        </div>
      </div>
    </div>
    <div class="filter-section" v-if="creators.length">
      <div class="container-title">
        <p class="title">{{ $t('dash.reporting.filter.creator') }} <span>({{ countCreatorsOn }})</span></p>
        <p class="reset" v-if="countCreatorsOn < creators.length" @click="addAllCreators">{{ $t('dash.posts.filter.show-all') }}</p>
        <p class="reset" v-else @click="removeAllCreators">{{ $t('dash.posts.filter.hide-all') }}</p>
      </div>
      <div class="list creator-list">
        <div class="type creator-row" v-for="creator in creators" :key="creator.id">
          <Checkbox :id="creator.id" prefixId="rfc" :isChecked="filter.creators[creator.id] !== false" :remove="function() { toggleCreator(creator.id) }" :add="function() { toggleCreator(creator.id) }" />
          <label :for="'rfc' + creator.id" class="creator-label">
            <span class="creator-name">{{ creator.name || creator.email }}</span>
            <span v-if="creator.name && creator.email" class="creator-email">{{ creator.email }}</span>
          </label>
        </div>
      </div>
    </div>
    <div class="filter-section" v-if="labels.length">
      <div class="container-title">
        <p class="title">{{ $t('dash.posts.filter.label') }}<span v-if="filter.labels && filter.labels.length"> ({{ filter.labels.length }})</span></p>
        <p class="reset" v-if="filter.labels && filter.labels.length" @click="clearLabels">{{ $t('dash.posts.filter.show-all') }}</p>
      </div>
      <div class="list label-list">
        <div class="type label-row" v-for="label in labels" :key="label.id">
          <Checkbox :id="'rfl' + label.id" prefixId="" :isChecked="isLabelSelected(label.id)" :remove="function() { toggleLabel(label.id) }" :add="function() { toggleLabel(label.id) }" />
          <label :for="'rfl' + label.id" class="label-row-label">
            <span class="label-dot" :style="{ backgroundColor: label.color }">
              <i v-if="parseIcon(label.icon) && parseIcon(label.icon).type === 'fa'" class="fa-solid" :class="`fa-${parseIcon(label.icon).value}`"></i>
              <span v-else-if="parseIcon(label.icon)" class="label-dot-emoji">{{ parseIcon(label.icon).value }}</span>
            </span>
            <span class="label-name">{{ label.name }}</span>
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Checkbox from '@/components/dash/utils/checkbox.vue'
import { parseLabelIcon } from '@/utils/labelIcon.js'

const TYPE_LIST = ['image', 'video', 'story', 'carousel', 'link', 'document', 'poll', 'text']

export default {
  name: 'ReportingFilter',
  components: { Checkbox },
  props: {
    filter: { type: Object, required: true },
    creators: { type: Array, default: function() { return [] } },
    labels: { type: Array, default: function() { return [] } },
    hideTypes: { type: Boolean, default: false },
  },
  data() {
    return {
      typeList: TYPE_LIST,
    }
  },
  computed: {
    countTypesOn() {
      var types = this.filter.types || {}
      return TYPE_LIST.filter(function(t) { return types[t] !== false }).length
    },
    countCreatorsOn() {
      var creators = this.filter.creators || {}
      return this.creators.filter(function(c) { return creators[c.id] !== false }).length
    },
  },
  methods: {
    parseIcon(icon) {
      return parseLabelIcon(icon)
    },
    isLabelSelected(id) {
      return !!(this.filter.labels && this.filter.labels.includes(id))
    },
    toggleLabel(id) {
      var current = (this.filter.labels || []).slice()
      var idx = current.indexOf(id)
      if (idx >= 0) current.splice(idx, 1)
      else current.push(id)
      this.$emit('update:filter', Object.assign({}, this.filter, { labels: current }))
    },
    clearLabels() {
      this.$emit('update:filter', Object.assign({}, this.filter, { labels: [] }))
    },
    toggleType(name) {
      var updated = Object.assign({}, this.filter, {
        types: Object.assign({}, this.filter.types, { [name]: this.filter.types[name] === false ? true : false }),
      })
      this.$emit('update:filter', updated)
    },
    toggleSolution() {
      var updated = Object.assign({}, this.filter, { solutionOnly: !this.filter.solutionOnly })
      this.$emit('update:filter', updated)
    },
    toggleCreator(id) {
      var current = this.filter.creators || {}
      this.$emit('update:filter', Object.assign({}, this.filter, {
        creators: Object.assign({}, current, { [id]: current[id] === false ? true : false }),
      }))
    },
    addAllTypes() {
      var types = {}
      TYPE_LIST.forEach(function(t) { types[t] = true })
      this.$emit('update:filter', Object.assign({}, this.filter, { types: types }))
    },
    removeAllTypes() {
      var types = {}
      TYPE_LIST.forEach(function(t) { types[t] = false })
      this.$emit('update:filter', Object.assign({}, this.filter, { types: types }))
    },
    addAllCreators() {
      var creators = {}
      this.creators.forEach(function(c) { creators[c.id] = true })
      this.$emit('update:filter', Object.assign({}, this.filter, { creators: creators }))
    },
    removeAllCreators() {
      var creators = {}
      this.creators.forEach(function(c) { creators[c.id] = false })
      this.$emit('update:filter', Object.assign({}, this.filter, { creators: creators }))
    },
  },
}
</script>

<style scoped>
.reporting-filter {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 200;
  background: #fff;
  border: 1px solid #EAEAEA;
  border-radius: 10px;
  box-shadow: 0 0 12px 0 rgba(121, 121, 121, 0.09);
  min-width: 280px;
  padding: 18px 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  cursor: default;
}

.filter-section + .filter-section {
  border-top: 1px solid #f0ecf4;
  padding-top: 16px;
  margin-top: -4px;
}

.container-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.title {
  font-size: 14px;
  font-weight: 600;
  color: #121826;
  margin: 0;
  line-height: 1;
}

.title span {
  color: #6B1F4D;
  font-size: 13px;
  font-weight: 400;
}

.reset {
  font-size: 13px;
  font-weight: 500;
  color: #6B1F4D;
  cursor: pointer;
  margin: 0;
  white-space: nowrap;
  line-height: 1;
}
.reset:hover { text-decoration: underline; }

.list {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
}

.source-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.creator-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.creator-row {
  align-items: flex-start;
}

.creator-label {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.creator-name {
  font-size: 13px;
  color: #121826;
  line-height: 1.2;
}

.creator-email {
  font-size: 11px;
  color: #9ba3b7;
  line-height: 1.2;
  text-transform: none;
}

.type {
  display: flex;
  align-items: center;
  gap: 8px;
}

.label-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.label-row-label {
  display: flex;
  align-items: center;
  gap: 6px;
}

.label-dot {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 4px;
  font-size: 8px;
  color: #fff;
  flex-shrink: 0;
}

.label-dot-emoji {
  font-size: 10px;
  line-height: 1;
}

.label-name {
  font-size: 13px;
  color: #121826;
}

.type label {
  text-transform: capitalize;
  color: #121826;
  font-size: 13px;
  line-height: 1;
  cursor: pointer;
  user-select: none;
  margin: 0;
}
</style>
